import { DecimalUtil } from '@orca-so/common-sdk'
import { MarketClient } from '@renec-foundation/escrow-sdk'
import { PublicKey } from '@solana/web3.js'
import { Decimal } from 'decimal.js'
import { useCallback } from 'react'
import { useQuery } from 'react-query'

import { isChainSolana } from '@/constants/index'
import useTokens, { RENGN_MINT_ADDRESS,REUSD_MINT_ADDRESS, REVND_MINT_ADDRESS } from '@/hooks/use-tokens'
import { toResultAsync } from '@/utils/helpers'

import useEscrowContext from './use-escrow-ctx'

type MarketClientMap = Record<string, MarketClient | undefined>
export type SupportedTokens = 'reVND' | 'reUSD' | 'reNGN'

// TODO: get from settings instead of hardcode
const tokens: SupportedTokens[] = isChainSolana() ? ['reUSD'] : ['reVND', 'reUSD', 'reNGN']
export const isMarketSupportedToken = (token: string): token is SupportedTokens =>
  tokens.includes(token as SupportedTokens)
const tokensMintAddress: Record<SupportedTokens, string> = {
  reVND: REVND_MINT_ADDRESS,
  reUSD: REUSD_MINT_ADDRESS,
  reNGN: RENGN_MINT_ADDRESS,
}
const BONDLESS_MAX_AMOUNT_LIMIT_IN_USD = 3

const useMarketClient = () => {
  const { context, configId, programId } = useEscrowContext()
  const { renecTokenInfo } = useTokens()

  const { data: marketClientMap } = useQuery<MarketClientMap>({
    queryKey: ['marketClientMap', configId, programId],
    queryFn: async () => {
      const [, marketDatas] = await toResultAsync(
        Promise.all(
          tokens.map(token => {
            return MarketClient.getMarket(
              context,
              configId,
              configId,
              new PublicKey(tokensMintAddress[token]),
            )
              // eslint-disable-next-line no-console
              .catch(console.error)
          }),
        ),
      )

      return (marketDatas || []).reduce((acc, marketData, idx) => {
        acc[tokens[idx]] = marketData as MarketClient
        return acc
      }, {} as MarketClientMap)
    },
    enabled: !!context,
    refetchInterval: 10000,
    initialData: {} as MarketClientMap,
  })

  const getMarketData = useCallback((tokenSymbol: SupportedTokens) => {
    const data = marketClientMap?.[tokenSymbol]?.data
    if (data) {
      const minimumOfferSize = DecimalUtil.fromU64(data.minimumOfferSize, data.tokenDecimals)
      const bondlessLimitAmount = DecimalUtil.adjustDecimals(
        new Decimal(BONDLESS_MAX_AMOUNT_LIMIT_IN_USD)
          .mul(data.tokenMetadata.usdRate.toNumber()),
        data.expo,
      ).toDP(data.tokenDecimals, Decimal.ROUND_DOWN)

      return {
        minimumOfferSize,
        bondlessLimitAmount,
        penaltyNativeBondIncrement: DecimalUtil.fromU64(data.penaltyNativeBondIncrement, renecTokenInfo.decimals),
      }
    }
  }, [marketClientMap, renecTokenInfo])

  return {
    marketClientMap,
    getMarketData,
  }
}

export default useMarketClient
